/**
 * A UI do widget e o "maestro" que liga o `flow-engine` aos executores de efeito.
 *
 * Esta função é exportada como `mountWidget` e é usada em **dois** lugares: no site do visitante
 * (via `widget.js`) e no preview do painel. Não existe uma segunda implementação da conversa —
 * era esse o ponto do planejamento, e vale tanto para o interpretador quanto para o visual.
 *
 * Decisões que ficam visíveis no código:
 *
 *  - **`textContent`, nunca `innerHTML`.** O texto do fluxo vem da config; Shadow DOM isola CSS,
 *    mas não impede `<script>` de rodar. Montar bolha por `innerHTML` transformaria o editor de
 *    fluxo num injetor de HTML na página do cliente.
 *  - **Shadow DOM `closed`.** O JS do site host não alcança o interior do widget por acidente.
 *  - **Uma geração por execução.** `reset()`/`destroy()` invalidam o laço de efeitos em andamento,
 *    então uma resposta de webhook que chega atrasada não escreve numa conversa já reiniciada.
 */

import {
  createEngine,
  validateInput,
  type Effect,
  type FlowEngine,
  type InputType,
  type TranscriptEntry,
  type Variables,
  type WebchatConfig,
} from '@merlin/flow-engine';
import { buildStyles } from '../styles.js';
import { runCode } from '../runtime/code.js';
import { runBackendWebhook, runBrowserWebhook } from '../runtime/webhook.js';
import { createSessionId } from '../transport.js';
import { createTelemetry, type Telemetry } from '../runtime/telemetry.js';
import { SDK_VERSION } from '../version.js';

export type WidgetMode = 'live' | 'preview';

export interface WidgetEvent {
  type: 'open' | 'close' | 'start' | 'end' | 'error' | 'variables';
  variables?: Variables;
  message?: string;
}

export interface MountOptions {
  config: WebchatConfig;
  /** Base das rotas públicas. Necessária para o bloco de webhook de backend. */
  apiBase?: string;
  /**
   * Origem que serve `/widget/assets/` — de onde vem a textura de fundo. Separada de `apiBase` de
   * propósito: `data-api-base` pode apontar para outro host, mas o asset mora onde o script mora.
   * O padrão cobre o preview do painel, que serve o widget da própria origem.
   */
  assetBase?: string;
  container?: HTMLElement;
  mode?: WidgetMode;
  autoOpen?: boolean;
  onEvent?: (event: WidgetEvent) => void;
}

export interface WidgetInstance {
  open(): void;
  close(): void;
  toggle(): void;
  reset(): void;
  destroy(): void;
  isOpen(): boolean;
  getVariables(): Variables;
  getTranscript(): TranscriptEntry[];
  readonly host: HTMLElement;
  /**
   * Raiz do Shadow DOM, para quem montou o widget (preview do painel e testes) inspecionar.
   *
   * Isto não afrouxa o `mode: 'closed'`: o objetivo de "closed" é que scripts da página host não
   * cheguem ao interior via `elemento.shadowRoot`, e eles continuam não chegando — a página host
   * nunca recebe esta instância. O que ela recebe é `window.MerlinWebchat`, que expõe apenas
   * `open/close/toggle/reset/getVariables/on`.
   */
  readonly shadow: ShadowRoot;
}

const WHATSAPP_GLYPH =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.66.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.27-.47-2.42-1.49-.9-.8-1.5-1.79-1.68-2.09-.17-.3-.02-.46.13-.61.15-.15.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5h-.56c-.2 0-.5.07-.77.37-.27.3-1.02 1-1.02 2.42s1.04 2.8 1.19 3c.15.2 2.05 3.28 5.02 4.47 2.97 1.19 2.97.79 3.5.74.54-.05 1.75-.72 2-1.41.24-.7.24-1.29.17-1.41-.07-.12-.27-.2-.57-.35z"/><path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.38 5.06L2 22l5.09-1.33A9.94 9.94 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm0 18.2c-1.6 0-3.09-.46-4.35-1.26l-.31-.19-3.02.79.8-2.94-.2-.32A8.15 8.15 0 0 1 3.8 12c0-4.52 3.68-8.2 8.2-8.2s8.2 3.68 8.2 8.2-3.68 8.2-8.2 8.2z"/></svg>';

/**
 * Tique duplo da bolha enviada.
 *
 * Decorativo: numa conversa com bot não há estado de entrega nem de leitura para representar. O
 * horário ao lado é real; este glifo existe porque é ele que faz a bolha ser lida como WhatsApp.
 */
const DOUBLE_TICK_GLYPH =
  '<svg viewBox="0 0 16 15" fill="currentColor" aria-hidden="true"><path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"/></svg>';

const INPUT_TYPE_ATTR: Record<InputType, string> = {
  text: 'text',
  email: 'email',
  phone: 'tel',
  number: 'text',
};

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function mountWidget(options: MountOptions): WidgetInstance {
  const { config } = options;
  const mode: WidgetMode = options.mode ?? 'live';
  const container = options.container ?? document.body;
  const appearance = config.appearance;
  const emit = (event: WidgetEvent): void => options.onEvent?.(event);

  // ----- estrutura -----
  const host = el('div');
  host.setAttribute('data-merlin-webchat', config.publicKey);
  /*
   * Versão no DOM, para dar para responder "a publicação já chegou?" olhando a página do cliente.
   *
   * Antes só existia `MerlinWebchat.version`, que é a versão do **SDK** e não muda quando se publica um
   * fluxo — justamente a pergunta que se quer responder. E ela vive no `window`, o que obriga a abrir o
   * console; atributo no host aparece no inspetor de elementos, que é onde a pessoa já está.
   *
   * - `data-merlin-config` é o número que **o Publicar incrementa**. É o que interessa: enquanto ele
   *   não virar o número novo, o cache ainda está servindo o antigo (ver `cache.ts` no engine para o
   *   prazo e as três camadas). `0` é o que o preview do painel usa, porque lá o rascunho é lido
   *   direto, sem versão publicada.
   * - `data-merlin-sdk` é a versão do widget, útil para saber se o site está fixado numa versão antiga.
   *
   * Ficam no host e não na raiz do Shadow DOM de propósito: o host é o único elemento nosso que a
   * página vê. `mode: 'closed'` continua valendo — atributo não é conteúdo, e nada aqui é segredo.
   */
  host.setAttribute('data-merlin-config', String(config.version ?? 0));
  host.setAttribute('data-merlin-sdk', SDK_VERSION);
  const shadow = host.attachShadow({ mode: 'closed' });

  const assetBase =
    options.assetBase ?? (typeof window === 'undefined' ? '' : window.location.origin);

  const style = document.createElement('style');
  style.textContent = buildStyles(appearance, assetBase);
  shadow.appendChild(style);

  const root = el('div', 'root');
  root.setAttribute('data-position', appearance.position);

  const launcher = el('button', 'launcher');
  launcher.type = 'button';
  launcher.setAttribute('aria-label', `Abrir conversa: ${appearance.title}`);
  if (appearance.imageUrl) {
    const img = el('img');
    img.src = appearance.imageUrl;
    img.alt = '';
    launcher.appendChild(img);
  } else {
    // Único uso de innerHTML no arquivo, e é sobre uma constante nossa — nada da config entra aqui.
    launcher.innerHTML = WHATSAPP_GLYPH;
  }

  const panel = el('div', 'panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', appearance.title);
  panel.dataset.open = 'false';
  // Espelhado na raiz: o CSS do modo compacto casa compacto **e** aberto no mesmo seletor.
  root.dataset.open = 'false';

  const header = el('div', 'header');
  const avatar = el('div', 'avatar');
  if (appearance.imageUrl) {
    const img = el('img');
    img.src = appearance.imageUrl;
    img.alt = '';
    avatar.appendChild(img);
  } else {
    avatar.innerHTML = WHATSAPP_GLYPH;
  }
  const titles = el('div', 'titles');
  titles.appendChild(el('span', 'title', appearance.title));
  if (appearance.subtitle) titles.appendChild(el('span', 'subtitle', appearance.subtitle));
  const closeButton = el('button', 'close', '×');
  closeButton.type = 'button';
  closeButton.setAttribute('aria-label', 'Fechar conversa');
  header.append(avatar, titles, closeButton);

  const messages = el('div', 'messages');
  messages.setAttribute('role', 'log');
  messages.setAttribute('aria-live', 'polite');

  const composer = el('div', 'composer');
  const optionsRow = el('div', 'options');
  const form = el('form', 'form');
  const input = el('input', 'input');
  input.type = 'text';
  input.autocomplete = 'off';
  const send = el('button', 'send', 'Enviar');
  send.type = 'submit';
  form.append(input, send);
  const hint = el('div', 'hint');
  composer.append(optionsRow, form, hint);

  const footer = el('div', 'footer');
  footer.hidden = true;

  panel.append(header, messages, composer, footer);
  root.append(panel, launcher);
  shadow.appendChild(root);
  container.appendChild(host);

  // ----- estado -----
  let engine: FlowEngine = createEngine(config);
  let sessionId = createSessionId();
  /*
   * Analytics e leads. No preview do painel fica desligado: conversa de teste não pode virar métrica
   * de produção nem gerar lead falso. Sem `apiBase` também fica desligado, porque não há para onde
   * mandar.
   */
  let telemetry: Telemetry = createTelemetry({
    apiBase: options.apiBase ?? '',
    publicKey: config.publicKey,
    sessionId,
    enabled: mode === 'live' && Boolean(options.apiBase),
  });
  let started = false;
  let open = false;
  let generation = 0;
  let destroyed = false;
  let awaitingInput: { saveAs: string; inputType: InputType } | null = null;
  const timers = new Set<ReturnType<typeof setTimeout>>();

  /*
   * ---------------------------------------------------------------------------
   * Modo compacto: em tela estreita a conversa ocupa a tela toda, como o app.
   * ---------------------------------------------------------------------------
   *
   * O gatilho e' medido aqui em JS, e nao por `@media (max-width: …)` no CSS. O preview do painel
   * simula um celular num palco de 390px **dentro de uma janela de desktop larga**: a media query
   * leria a janela real, e o fullscreen so apareceria em producao — comportamento que nao da' para
   * ver antes de publicar e' exatamente o que este projeto evita.
   */
  const COMPACT_MAX_WIDTH = 480;
  let compact = false;
  let observadorDePalco: ResizeObserver | null = null;
  /** Valor de `body.overflow` do site host antes de travarmos. `null` = nao travamos nada. */
  let overflowAnterior: string | null = null;

  /**
   * O palco, quando existe um e da' para medir.
   *
   * O `container` padrao e' `document.body`, que nao serve: `body.clientHeight` e' a altura do
   * **conteudo**, nao da tela — num site longo daria milhares de pixels. Por isso a checagem e' no
   * `options.container` cru, que so' vem preenchido quando o host montou o widget numa caixa propria.
   */
  function palcoMedivel(): HTMLElement | null {
    const palco = options.container;
    return palco && palco.clientWidth > 0 ? palco : null;
  }

  /**
   * Viewport efetiva. Com `container` proprio (o palco do preview), vale a caixa dele; senao, a
   * janela.
   */
  function viewportSize(): { width: number; height: number } {
    const palco = palcoMedivel();
    if (palco) return { width: palco.clientWidth, height: palco.clientHeight };
    if (typeof window === 'undefined') return { width: Number.POSITIVE_INFINITY, height: 0 };
    return { width: window.innerWidth, height: window.innerHeight };
  }

  /**
   * Publica a viewport efetiva como `--merlin-viewport-*` no host, para o **CSS** ver o mesmo numero
   * que o JS.
   *
   * Defeito real que isto conserta, e que existia desde que o preview ganhou tela propria: no modo
   * Computador o palco do painel nao declarava essas propriedades, entao a altura da janela caia no
   * fallback `100vh` — a janela do navegador inteira. O palco e' mais baixo que isso (o cabecalho do
   * painel e o respiro de 24px de cada lado comem uns 110px), e a janela de chat, ancorada embaixo,
   * transbordava por cima: sumia justamente o cabecalho dela, recortado pelo `overflow: hidden` do
   * palco. Com a conta certa, o topo volta a ter os 24px de `TOP_BREATHING_ROOM` que o desenho pede.
   *
   * Mora aqui, e nao no palco do painel, por dois motivos. O JS **ja'** media o container para decidir
   * o modo compacto, então a medida existe e estava sendo jogada fora pelo CSS; e qualquer host que
   * monte o widget numa caixa propria — nao so' o nosso preview — ganha a conta certa sem precisar
   * declarar nada.
   *
   * Sem palco medivel, **remove** em vez de escrever: e' o que deixa valer o `100vw/100vh` do CSS num
   * site de verdade, e o que faz o preview cair de volta na declaracao do proprio palco (que herda
   * pelo ancestral) enquanto a primeira medicao ainda nao aconteceu.
   */
  function publicarViewport(): void {
    const palco = palcoMedivel();
    if (!palco) {
      host.style.removeProperty('--merlin-viewport-width');
      host.style.removeProperty('--merlin-viewport-height');
      return;
    }
    host.style.setProperty('--merlin-viewport-width', `${palco.clientWidth}px`);
    host.style.setProperty('--merlin-viewport-height', `${palco.clientHeight}px`);
  }

  /**
   * Trava a rolagem do site host enquanto a conversa ocupa a tela.
   *
   * Guarda o valor anterior em vez de assumir `''`: o site pode ter o proprio `overflow` definido, e
   * devolver a coisa errada quebraria a pagina dele depois que o widget saisse de cena.
   *
   * Nao vale no preview: ali o "celular" e' um palco dentro do painel, e travar a rolagem travaria a
   * pagina do **painel**, que nao tem nada a ver com a tela simulada.
   */
  function travarRolagem(travar: boolean): void {
    if (typeof document === 'undefined' || mode !== 'live') return;
    if (travar) {
      if (overflowAnterior === null) overflowAnterior = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return;
    }
    if (overflowAnterior !== null) {
      document.body.style.overflow = overflowAnterior;
      overflowAnterior = null;
    }
  }

  function syncCompact(): void {
    // Antes de decidir o modo: as duas coisas saem da mesma medida, e o CSS precisa dela.
    publicarViewport();
    compact = viewportSize().width <= COMPACT_MAX_WIDTH;
    if (compact) root.dataset.compact = 'true';
    else delete root.dataset.compact;
    travarRolagem(compact && open);
  }

  function track<T>(promise: Promise<T>): Promise<T> {
    return promise;
  }

  function scheduleTimer(fn: () => void, ms: number): void {
    const timer = setTimeout(() => {
      timers.delete(timer);
      fn();
    }, ms);
    timers.add(timer);
  }

  function scrollToEnd(): void {
    messages.scrollTop = messages.scrollHeight;
  }

  /**
   * `true` quando esta bolha abre uma sequência — é ela que ganha o rabicho, como no WhatsApp.
   *
   * A bolha de "digitando" é ignorada de propósito: ela é `role="bot"` e some depois, então contá-la
   * faria a mensagem que a substitui parecer continuação de uma sequência que nunca existiu.
   */
  function abreSequencia(role: string): boolean {
    const anteriores = [...messages.children].filter((node) => !node.querySelector('.typing'));
    const ultima = anteriores[anteriores.length - 1] as HTMLElement | undefined;
    return ultima?.dataset.role !== role;
  }

  /** `06:52` no relógio do visitante. */
  function horaAgora(): string {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  function addBubble(role: 'bot' | 'visitor' | 'system', text: string): HTMLElement {
    const bubble = el('div', 'bubble');
    bubble.dataset.role = role;
    if (abreSequencia(role)) bubble.dataset.first = 'true';

    const corpo = el('span', 'texto');
    corpo.textContent = text; // nunca innerHTML: o texto vem da config
    bubble.appendChild(corpo);

    // Aviso de sistema não é mensagem de conversa: sem horário e sem tique.
    if (role !== 'system') {
      const meta = el('span', 'meta');
      meta.append(horaAgora());
      if (role === 'visitor') {
        const tique = el('span');
        // Mesmo caso do glifo do launcher: constante nossa, nada da config passa por aqui.
        tique.innerHTML = DOUBLE_TICK_GLYPH;
        meta.appendChild(tique);
      }
      bubble.appendChild(meta);
    }

    messages.appendChild(bubble);
    scrollToEnd();
    return bubble;
  }

  function showTyping(): HTMLElement {
    const bubble = el('div', 'bubble');
    bubble.dataset.role = 'bot';
    const typing = el('div', 'typing');
    typing.append(el('span'), el('span'), el('span'));
    bubble.appendChild(typing);
    messages.appendChild(bubble);
    scrollToEnd();
    return bubble;
  }

  function setComposer(state: 'input' | 'choice' | 'waiting' | 'ended'): void {
    form.hidden = state !== 'input';
    send.disabled = state !== 'input';
    input.disabled = state !== 'input';
    if (state !== 'choice') optionsRow.replaceChildren();
    if (state !== 'input') {
      input.value = '';
      hint.textContent = '';
      awaitingInput = null;
    }
  }

  function finish(reason: string): void {
    setComposer('ended');
    footer.hidden = false;
    footer.replaceChildren();
    footer.appendChild(document.createTextNode(reason));
    const restart = el('button', 'option', 'Começar de novo');
    restart.type = 'button';
    restart.style.marginTop = '8px';
    restart.addEventListener('click', () => instance.reset());
    footer.appendChild(el('div'));
    footer.appendChild(restart);
  }

  /**
   * Executa a lista de efeitos em ordem, respeitando o atraso de digitação.
   * `generation` garante que uma execução antiga não escreva numa conversa reiniciada.
   */
  async function processEffects(effects: Effect[], myGeneration: number): Promise<void> {
    for (const effect of effects) {
      if (destroyed || myGeneration !== generation) return;

      // Passagem por bloco: é daqui que sai o abandono por nó, o número mais útil do analytics.
      if ('nodeId' in effect && effect.nodeId) telemetry.node(effect.nodeId);

      switch (effect.kind) {
        case 'message': {
          const typingDelay = Math.min(appearance.typingDelayMs ?? 0, 3000);
          if (typingDelay > 0) {
            const placeholder = showTyping();
            await track(delay(typingDelay));
            if (destroyed || myGeneration !== generation) return;
            placeholder.remove();
          }
          addBubble('bot', effect.text);
          break;
        }

        case 'awaitInput': {
          addBubble('bot', effect.prompt);
          awaitingInput = { saveAs: effect.saveAs, inputType: effect.inputType };
          setComposer('input');
          input.type = INPUT_TYPE_ATTR[effect.inputType] ?? 'text';
          input.placeholder = effect.placeholder ?? 'Escreva sua resposta…';
          if (open) input.focus();
          return;
        }

        case 'awaitChoice': {
          addBubble('bot', effect.prompt);
          setComposer('choice');
          optionsRow.replaceChildren();
          for (const option of effect.options) {
            const button = el('button', 'option', option.label);
            button.type = 'button';
            button.addEventListener('click', () => {
              addBubble('visitor', option.label);
              optionsRow.replaceChildren();
              const next = engine.submitChoice(option.id);
              void processEffects(next, generation);
            });
            optionsRow.appendChild(button);
          }
          scrollToEnd();
          return;
        }

        case 'runCode': {
          setComposer('waiting');
          const placeholder = showTyping();
          const result = await track(
            runCode(effect, engine.getVariables(), {
              // No preview, código de terceiros não roda no contexto do painel.
              forceSandbox: mode === 'preview',
            }),
          );
          if (destroyed || myGeneration !== generation) return;
          placeholder.remove();
          if (mode === 'preview' && effect.executionMode === 'page') {
            addBubble(
              'system',
              'Este bloco está em modo `page`. No preview ele roda no sandbox — no site publicado ' +
                'terá acesso completo à página.',
            );
          }
          const next = engine.resolveEffect(effect.nodeId, result);
          await processEffects(next, myGeneration);
          return;
        }

        case 'browserWebhook': {
          setComposer('waiting');
          const placeholder = showTyping();
          const result = await track(runBrowserWebhook(effect.request));
          if (destroyed || myGeneration !== generation) return;
          placeholder.remove();
          const next = engine.resolveEffect(effect.nodeId, result);
          await processEffects(next, myGeneration);
          return;
        }

        case 'backendWebhook': {
          setComposer('waiting');
          const placeholder = showTyping();

          if (mode === 'preview' || !options.apiBase) {
            // No preview o servidor só conhece a versão publicada, então não há o que executar
            // com o draft em edição. Dizer isso é melhor que devolver um erro enigmático.
            placeholder.remove();
            addBubble(
              'system',
              mode === 'preview'
                ? 'Webhook de backend não roda no preview: o servidor executa a partir da versão publicada.'
                : 'Webhook de backend indisponível: o widget foi montado sem `apiBase`.',
            );
            const next = engine.resolveEffect(effect.nodeId, { ok: true, value: null });
            await processEffects(next, myGeneration);
            return;
          }

          const result = await track(
            runBackendWebhook({
              apiBase: options.apiBase,
              publicKey: config.publicKey,
              nodeId: effect.nodeId,
              variables: engine.getVariables(),
              sessionId,
            }),
          );
          if (destroyed || myGeneration !== generation) return;
          placeholder.remove();
          const next = engine.resolveEffect(effect.nodeId, result);
          await processEffects(next, myGeneration);
          return;
        }

        case 'error': {
          addBubble('system', effect.message);
          emit({ type: 'error', message: effect.message });
          break;
        }

        case 'end': {
          finish('Conversa finalizada.');
          telemetry.complete();
          /*
           * Lead ao fim da conversa. O `collectLeads` da config só evita a requisição inútil quando
           * está desligado — quem decide de verdade é o servidor, que relê a configuração do webchat.
           */
          if (config.collectLeads !== false) {
            telemetry.submitLead(engine.getVariables(), engine.getTranscript());
          }
          emit({ type: 'end', variables: engine.getVariables() });
          return;
        }

        default:
          break;
      }
    }

    emit({ type: 'variables', variables: engine.getVariables() });
  }

  function submitCurrentInput(): void {
    if (!awaitingInput) return;
    const value = input.value;
    const validation = validateInput(awaitingInput.inputType, value);
    if (!validation.ok) {
      hint.textContent = validation.error ?? 'Resposta inválida.';
      return;
    }
    hint.textContent = '';
    const trimmed = value.trim();
    addBubble('visitor', trimmed);
    input.value = '';
    setComposer('waiting');
    const next = engine.submitInput(trimmed);
    void processEffects(next, generation);
  }

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    submitCurrentInput();
  });

  input.addEventListener('input', () => {
    if (hint.textContent) hint.textContent = '';
  });

  const instance: WidgetInstance = {
    open(): void {
      if (destroyed) return;
      open = true;
      panel.dataset.open = 'true';
      // Espelhado na raiz porque o CSS do modo compacto precisa casar compacto **e** aberto no
      // mesmo seletor — ver o bloco `[data-compact][data-open]` em styles.ts.
      root.dataset.open = 'true';
      launcher.setAttribute('aria-label', `Fechar conversa: ${appearance.title}`);
      // Remede a viewport ao abrir: a janela pode ter mudado de tamanho com o painel fechado.
      syncCompact();
      emit({ type: 'open' });
      if (!started) {
        started = true;
        telemetry.start();
        emit({ type: 'start' });
        void processEffects(engine.start(), generation);
      } else if (awaitingInput) {
        input.focus();
      }
    },

    close(): void {
      open = false;
      panel.dataset.open = 'false';
      root.dataset.open = 'false';
      launcher.setAttribute('aria-label', `Abrir conversa: ${appearance.title}`);
      travarRolagem(false);
      emit({ type: 'close' });
    },

    toggle(): void {
      if (open) instance.close();
      else instance.open();
    },

    reset(): void {
      generation += 1;
      engine = createEngine(config);
      // Sessão nova: recomeçar a conversa é outra sessão, e a anterior conta como abandonada.
      telemetry.destroy();
      sessionId = createSessionId();
      telemetry = createTelemetry({
        apiBase: options.apiBase ?? '',
        publicKey: config.publicKey,
        sessionId,
        enabled: mode === 'live' && Boolean(options.apiBase),
      });
      started = false;
      awaitingInput = null;
      messages.replaceChildren();
      optionsRow.replaceChildren();
      footer.hidden = true;
      footer.replaceChildren();
      setComposer('waiting');
      if (open) {
        started = true;
        telemetry.start();
        void processEffects(engine.start(), generation);
      }
    },

    destroy(): void {
      destroyed = true;
      telemetry.destroy();
      generation += 1;
      for (const timer of timers) clearTimeout(timer);
      timers.clear();
      /*
       * Devolver a rolagem aqui e' o ponto sensivel de tudo isto: falhar deixa o site do cliente
       * travado depois que o widget saiu de cena, sem nada na pagina apontando para a causa.
       */
      travarRolagem(false);
      if (typeof window !== 'undefined') window.removeEventListener('resize', syncCompact);
      observadorDePalco?.disconnect();
      host.remove();
    },

    isOpen: () => open,
    getVariables: () => engine.getVariables(),
    getTranscript: () => engine.getTranscript(),
    host,
    shadow,
  };

  launcher.addEventListener('click', () => instance.toggle());
  closeButton.addEventListener('click', () => instance.close());
  panel.addEventListener('keydown', (event) => {
    if ((event as KeyboardEvent).key === 'Escape') instance.close();
  });

  /*
   * Dois caminhos porque nenhum cobre os dois cenarios:
   *
   * - `resize` da janela e' o caso do site real, girar o aparelho incluso;
   * - o observador no palco e' o caso do preview trocando Desktop/Celular, onde a janela do
   *   navegador nao muda de tamanho nenhum e o `resize` nunca dispara. Trocar de dispositivo la' nao
   *   remonta o widget de proposito (nao se perde a conversa), entao medir so na montagem nao serve.
   */
  syncCompact();
  if (typeof window !== 'undefined') window.addEventListener('resize', syncCompact);
  observadorDePalco =
    options.container && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => syncCompact())
      : null;
  observadorDePalco?.observe(options.container as Element);

  setComposer('waiting');

  const autoOpenDelay = options.autoOpen ? 0 : appearance.autoOpenDelayMs ?? 0;
  if (options.autoOpen) {
    instance.open();
  } else if (autoOpenDelay > 0) {
    scheduleTimer(() => {
      if (!destroyed && !open) instance.open();
    }, autoOpenDelay);
  }

  return instance;
}
